// frontend/src/components/BlogManagement.js
import React, { useState, useEffect, useContext } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { ChatIcon, DeleteIcon, EditIcon } from './Icons';

const BlogManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, API_URL } = useContext(AuthContext);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
        const { data } = await axios.get(`${API_URL}/blog/manage`, config);
        setPosts(data);
        setLoading(false);
      } catch (err) {
        setError('Blog posts could not be loaded.');
        setLoading(false);
      }
    };
    if (user && (user.role === 'admin' || user.role === 'gruppenleiter')) {
      fetchPosts();
    }
  }, [user, API_URL]);

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return;
    }

    const post = posts.find((p) => p._id === draggableId);
    const newPosts = Array.from(posts);
    newPosts.splice(source.index, 1);
    newPosts.splice(destination.index, 0, post);

    const updatedPost = { ...post, isArchived: destination.droppableId === 'archived' };
    setPosts(newPosts.map((p) => (p._id === draggableId ? updatedPost : p)));

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      await axios.put(`${API_URL}/blog/${draggableId}/archive`, {}, config);
    } catch (err) {
      setError('Error updating blog post status.');
      // Revert state if API call fails
      setPosts(posts);
    }
  };

  if (loading) return (
    <div className="flex items-center gap-3 p-4 bg-surface-containerLow rounded-xl animate-pulse">
      <div className="md3-progress-circular w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span className="text-surface-onVariant font-medium">Lade Beiträge...</span>
    </div>
  );
  if (error) return (
    <div className="p-4 bg-error-container text-error-on-container rounded-xl flex items-center gap-2 border border-error/20">
      <span>⚠️</span>
      <span className="font-bold">{error}</span>
    </div>
  );

  const pendingPosts = posts.filter((p) => p.status === 'pending' && !p.isArchived);
  const activePosts = posts.filter((p) => p.status === 'published' && !p.isArchived);
  const archivedPosts = posts.filter((p) => p.isArchived);

  const handlePublish = async (postId) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      await axios.put(`${API_URL}/blog/${postId}/publish`, {}, config);
      setPosts(posts.map(p => p._id === postId ? { ...p, status: 'published' } : p));
    } catch (err) {
      setError('Fehler beim Veröffentlichen des Beitrags.');
    }
  };

  const handleDelete = async (postId) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      await axios.delete(`${API_URL}/blog/${postId}`, config);
      setPosts(posts.filter(p => p._id !== postId));
      setDeleteConfirm(null);
    } catch (err) {
      setError('Fehler beim Löschen des Beitrags.');
    }
  };

  return (
    <div className="space-y-8">
      {/* Pending Section */}
      {pendingPosts.length > 0 && (
        <section className="bg-secondary-container/30 rounded-2xl border border-secondary/20 p-6 animate-fade-in">
          <div className="flex items-center justify-between mb-6 pb-2 border-b border-secondary/20">
            <h2 className="text-title-medium font-bold text-secondary-onContainer flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-secondary animate-pulse"></span>
              Ausstehende Beiträge zur Review
            </h2>
            <span className="text-label-small bg-secondary text-secondary-on px-2 py-1 rounded-full font-bold">
              {pendingPosts.length}
            </span>
          </div>
          <div className="grid gap-4">
            {pendingPosts.map((post) => (
              <div key={post._id} className="p-4 bg-surface-containerHighest rounded-xl border border-outlineVariant flex justify-between items-center shadow-sm">
                <div>
                  <h4 className="font-bold text-surface-on">{post.title}</h4>
                  <p className="text-label-small text-surface-onVariant">
                    Von {post.author?.userData?.scoutName || post.author?.userData?.firstName || 'Unbekannt'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Link 
                    to={`/messages?startWith=${post.author?._id}&refType=blogpost&refId=${post._id}`}
                    className="md3-btn-text text-primary h-9 px-4 font-bold hover:bg-primary/8 gap-1"
                    onClick={e => e.stopPropagation()}
                  >
                    <ChatIcon style={{ fontSize: 16 }} /> Rückmeldung
                  </Link>
                  <Link to={`/blog/edit/${post._id}`} className="md3-btn-text text-secondary h-9 px-4 font-bold hover:bg-secondary/8">
                    Review
                  </Link>
                  <button
                    onClick={() => handlePublish(post._id)}
                    className="md3-btn-tonal bg-secondary-container text-secondary-onContainer h-9 px-4 rounded-lg font-bold hover:bg-secondary-container/80 transition-all shadow-sm"
                  >
                    Veröffentlichen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Drag & Drop Management */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex flex-col lg:flex-row gap-8">
          <Droppable droppableId="active">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="flex-1 bg-surface-container rounded-2xl border border-outlineVariant p-6 shadow-sm transition-all">
                <div className="flex items-center justify-between mb-6 pb-2 border-b border-outlineVariant">
                  <h2 className="text-title-medium font-bold text-surface-on">Veröffentlicht</h2>
                  <span className="text-label-small bg-primary-container text-primary-onContainer px-2 py-1 rounded-full font-bold">
                    {activePosts.length}
                  </span>
                </div>
                <div className="space-y-4 min-h-[120px]">
                  {activePosts.map((post, index) => (
                    <Draggable key={post._id} draggableId={post._id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`p-5 bg-surface-containerHighest rounded-xl border border-outlineVariant shadow-sm flex justify-between items-center group ${snapshot.isDragging ? 'shadow-elevation-3 ring-2 ring-primary border-primary z-50' : 'hover:border-primary/40 hover:shadow-elevation-1 transition-all'}`}
                          style={{ ...provided.draggableProps.style }}
                        >
                          <span className="font-bold text-surface-on">{post.title}</span>
                          <div className="flex gap-2">
                            <Link to={`/blog/edit/${post._id}`} className="md3-btn-text h-8 px-4 text-primary font-bold group-hover:bg-primary/8">
                              Bearbeiten
                            </Link>
                            {deleteConfirm === post._id ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleDelete(post._id)} className="md3-btn-filled !bg-error !text-error-on h-8 px-3 text-[11px] font-bold uppercase">Löschen</button>
                                <button onClick={() => setDeleteConfirm(null)} className="md3-btn-text h-8 px-2 text-[11px]">Abbruch</button>
                              </div>
                            ) : (
                              <button onClick={() => setDeleteConfirm(post._id)} className="md3-icon-btn h-8 w-8 text-error/60 hover:text-error hover:bg-error/10">
                                <DeleteIcon style={{ fontSize: 18 }} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>

          <Droppable droppableId="archived">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="flex-1 bg-surface-containerLow rounded-2xl border border-outlineVariant p-6 shadow-sm grayscale-[0.3] opacity-90 transition-all">
                <div className="flex items-center justify-between mb-6 pb-2 border-b border-outlineVariant">
                  <h2 className="text-title-medium font-bold text-surface-onVariant">Archiv</h2>
                  <span className="text-label-small bg-surface-containerHighest text-surface-onVariant px-2 py-1 rounded-full font-bold">
                    {archivedPosts.length}
                  </span>
                </div>
                <div className="space-y-4 min-h-[120px]">
                  {archivedPosts.map((post, index) => (
                    <Draggable key={post._id} draggableId={post._id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`p-5 bg-surface-container rounded-xl border border-outlineVariant flex justify-between items-center opacity-60 grayscale-[0.5] ${snapshot.isDragging ? 'shadow-elevation-3 ring-2 ring-outline border-outline opacity-100 grayscale-0 z-50' : 'hover:opacity-100 transition-all'}`}
                          style={{ ...provided.draggableProps.style }}
                        >
                          <span className="font-medium text-surface-onVariant">{post.title}</span>
                          <div className="flex gap-2">
                            <Link to={`/blog/edit/${post._id}`} className="md3-btn-text h-8 px-4 text-secondary font-bold hover:bg-secondary/8">
                              Bearbeiten
                            </Link>
                            {deleteConfirm === post._id ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleDelete(post._id)} className="md3-btn-filled !bg-error !text-error-on h-8 px-3 text-[11px] font-bold uppercase">Löschen</button>
                                <button onClick={() => setDeleteConfirm(null)} className="md3-btn-text h-8 px-2 text-[11px]">Abbruch</button>
                              </div>
                            ) : (
                              <button onClick={() => setDeleteConfirm(post._id)} className="md3-icon-btn h-8 w-8 text-error/60 hover:text-error hover:bg-error/10">
                                <DeleteIcon style={{ fontSize: 18 }} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
  );
};

export default BlogManagement;
